import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, X, Download, Inbox } from "lucide-react";
import { btnOutline, cardCls, inputCls } from "./ui";

/**
 * Compact data table with search, dropdown filters, optional date range, sortable columns,
 * page-size + pagination and client-side CSV export.
 *
 * columns: [{ key, header, accessor?(row), cell?(row), sortable?, align?, className?, hideBelow? }]
 * filters: [{ key, label, accessor?(row), options?: [{value,label}] }]  (options derived from data if omitted)
 */
const DAYS = (s) => (s ? String(s).slice(0, 10) : "");
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function cmp(a, b) {
  if (a == null || a === "") return b == null || b === "" ? 0 : 1;   // blanks last
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export default function DataTable({
  columns, data = [], rowKey = "id", searchKeys, searchPlaceholder = "Search…", filters = [], dateField,
  defaultSort, pageSizes = [5, 10, 25, 50, 100], defaultPageSize = 10, loading = false, onRowClick, rowTestId, rowClassName,
  toolbarRight, emptyIcon: EmptyIcon = Inbox, emptyTitle = "Nothing here yet", emptyText, exportName, testId, searchTestId,
}) {
  const [query, setQuery] = useState("");
  const [fv, setFv] = useState({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState(defaultSort || null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const getVal = (col, row) => (col.accessor ? col.accessor(row) : row[col.key]);
  const keyFns = useMemo(
    () => (searchKeys || columns.filter((c) => c.header).map((c) => c.key)).map((k) => (typeof k === "function" ? k : (r) => r[k])),
    [searchKeys, columns],
  );

  // derive select options from data when not given
  const filterDefs = useMemo(() => filters.map((f) => {
    const get = f.accessor || ((r) => r[f.key]);
    const options = f.options || [...new Set(data.map(get).filter((v) => v !== undefined && v !== null && v !== ""))].sort().map((v) => ({ value: String(v), label: String(v).replace(/_/g, " ") }));
    return { ...f, get, options };
  }), [filters, data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((row) => {
      if (q && !keyFns.some((fn) => String(fn(row) ?? "").toLowerCase().includes(q))) return false;
      for (const f of filterDefs) {
        const want = fv[f.key];
        if (want && String(f.get(row)) !== want) return false;
      }
      if (dateField && (from || to)) {
        const d = DAYS(dateField.accessor(row));
        if (!d || (from && d < from) || (to && d > to)) return false;
      }
      return true;
    });
  }, [data, query, fv, from, to, keyFns, filterDefs, dateField]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => dir * cmp(getVal(col, a), getVal(col, b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, columns]);

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => { setPage(1); }, [query, fv, from, to, pageSize]);
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const start = (page - 1) * pageSize;
  const rows = sorted.slice(start, start + pageSize);

  const activeFilters = Boolean(query || from || to || Object.values(fv).some(Boolean));
  const clearAll = () => { setQuery(""); setFv({}); setFrom(""); setTo(""); };

  const toggleSort = (key) => setSort((s) => (!s || s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));

  const exportCsv = () => {
    const cols = columns.filter((c) => c.header);
    const lines = [cols.map((c) => csvCell(c.header)).join(",")];
    sorted.forEach((r) => lines.push(cols.map((c) => csvCell(c.exportValue ? c.exportValue(r) : getVal(c, r))).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `${exportName}.csv` });
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // compact page list with ellipses
  const pageList = useMemo(() => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const set = new Set([1, pages, page - 1, page, page + 1]);
    const arr = [...set].filter((p) => p >= 1 && p <= pages).sort((a, b) => a - b);
    return arr.flatMap((p, i) => (i && p - arr[i - 1] > 1 ? ["…", p] : [p]));
  }, [pages, page]);

  const hide = { md: "hidden md:table-cell", lg: "hidden lg:table-cell", sm: "hidden sm:table-cell" };
  const alignCls = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left");
  const selCls = `${inputCls} !w-auto min-w-[120px] pr-7`;

  return (
    <div className={`${cardCls} overflow-hidden`} data-testid={testId ? `${testId}-wrap` : undefined}>
      {/* Toolbar */}
      <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={searchPlaceholder} data-testid={searchTestId}
            className={`${inputCls} pl-8`} aria-label="Search" />
        </div>
        {filterDefs.map((f) => (
          <select key={f.key} value={fv[f.key] || ""} onChange={(e) => setFv((p) => ({ ...p, [f.key]: e.target.value }))} className={selCls} aria-label={f.label}
            data-testid={testId ? `${testId}-filter-${f.key}` : undefined}>
            <option value="">{f.label}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
        {dateField && (
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${inputCls} !w-[132px]`} aria-label={`${dateField.label} from`} />
            <span>to</span>
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={`${inputCls} !w-[132px]`} aria-label={`${dateField.label} to`} />
          </div>
        )}
        {activeFilters && (
          <button onClick={clearAll} className="inline-flex items-center gap-1 h-9 px-2.5 rounded-lg text-[12px] font-semibold text-slate-600 hover:text-red-600 hover:bg-red-50"><X size={13} /> Clear</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {toolbarRight}
          {exportName && (
            <button onClick={exportCsv} disabled={!sorted.length} className={btnOutline} data-testid={testId ? `${testId}-export` : undefined}><Download size={14} /> Export</button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[13px]" data-testid={testId}>
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-[12px] font-semibold">
              {columns.map((c) => {
                const sortable = c.sortable !== false && c.header;
                const active = sort?.key === c.key;
                return (
                  <th key={c.key} scope="col" aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                    className={`px-4 py-2.5 whitespace-nowrap ${alignCls(c.align)} ${c.hideBelow ? hide[c.hideBelow] : ""} ${c.headerClassName || ""}`}>
                    {sortable ? (
                      <button onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 hover:text-blue-600 ${active ? "text-blue-600" : ""}`}>
                        {c.header}
                        {active ? (sort.dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : <ChevronsUpDown size={12} className="text-slate-300" />}
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-t border-slate-100">
                {columns.map((c) => <td key={c.key} className="px-4 py-3"><div className="h-3.5 rounded bg-slate-100 animate-pulse" style={{ width: `${55 + ((i * 17) % 35)}%` }} /></td>)}
              </tr>
            ))}
            {!loading && rows.map((r) => (
              <tr key={typeof rowKey === "function" ? rowKey(r) : r[rowKey]} onClick={onRowClick ? () => onRowClick(r) : undefined}
                data-testid={rowTestId?.(r)}
                className={`border-t border-slate-100 hover:bg-blue-50/40 transition-colors ${onRowClick ? "cursor-pointer" : ""} ${rowClassName?.(r) || ""}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-2 text-neutral-800 ${alignCls(c.align)} ${c.hideBelow ? hide[c.hideBelow] : ""} ${c.className || ""}`}>
                    {c.cell ? c.cell(r) : (getVal(c, r) ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-12 text-center">
                <EmptyIcon size={30} className="mx-auto text-slate-300" />
                <div className="mt-2 text-[13px] font-semibold text-neutral-800">{activeFilters ? "No results match your filters" : emptyTitle}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">{activeFilters ? "Try changing or clearing the filters." : emptyText}</div>
                {activeFilters && <button onClick={clearAll} className={`${btnOutline} mt-3`}>Clear filters</button>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="px-4 py-2.5 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-[12px] text-slate-600">
        <div data-testid={testId ? `${testId}-count` : undefined}>
          {sorted.length === 0 ? "No entries" : `Showing ${start + 1}–${Math.min(start + pageSize, sorted.length)} of ${sorted.length}`}
          {sorted.length !== data.length && <span className="text-slate-400"> (filtered from {data.length})</span>}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5">Rows
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className={`${inputCls} !h-8 !w-[68px] !text-[12px]`} aria-label="Rows per page">
              {pageSizes.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page"
              className="w-8 h-8 rounded-lg border border-slate-300 bg-white flex items-center justify-center hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:hover:border-slate-300 disabled:hover:text-inherit"><ChevronLeft size={15} /></button>
            {pageList.map((p, i) => p === "…" ? <span key={`e${i}`} className="px-1 text-slate-400">…</span> : (
              <button key={p} onClick={() => setPage(p)} aria-current={p === page ? "page" : undefined}
                className={`min-w-8 h-8 px-2 rounded-lg border text-[12px] font-semibold ${p === page ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 bg-white hover:border-blue-300 hover:text-blue-600"}`}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages} aria-label="Next page"
              className="w-8 h-8 rounded-lg border border-slate-300 bg-white flex items-center justify-center hover:border-blue-300 hover:text-blue-600 disabled:opacity-40 disabled:hover:border-slate-300 disabled:hover:text-inherit"><ChevronRight size={15} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
